import type { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import type { DeepReadonlyObject } from "rxdb"
import { useEntryModalPrivateContext } from "../EntryModalPrivateContext"
import { usePolyglot } from "src/contexts"
import { useAccountInsulins } from "src/hooks"
import type {
  AccountInsulin,
  EditableSubEntry,
} from "src/types"
import { insulinPrecisionToUnits } from "src/utils"

interface Option {
  value: string;
  children: string;
  disabled?: boolean;
}

const polyglotPrefix = "components.entry_modal.insulin.name."

export function useNameSelectOptions(): Array<Option> {
  const polyglot = usePolyglot()
  const { result: accountInsulins } = useAccountInsulins()

  const { originalSubEntry } = useEntryModalPrivateContext()
  const originalInsulin = (originalSubEntry as EditableSubEntry<ActivityType.Insulin> | undefined)?.insulin?.insulin_med

  // calculate the options for the select menu
  const selectOptions: Array<Option> = useMemo(
    () => {
      // the default option
      const defaultOption: Option = {
        value: "",
        children: polyglot.t(`${polyglotPrefix}placeholder`),
        disabled: true,
      }

      let activityHasMatch = false
      const optionsArray: Array<Option> = accountInsulins.map((accountInsulin: DeepReadonlyObject<AccountInsulin>) => {
        const insulinName = accountInsulin.name_override
          ?? accountInsulin.insulin.name
          ?? polyglot.t(`${polyglotPrefix}name_fallback`)

        const precision = polyglot.t(
          "unit.num_units_abbreviated",
          { units: insulinPrecisionToUnits(accountInsulin.precision).toLocaleString(polyglot.locale()) },
        )

        const insulinString = polyglot.t(
          `${polyglotPrefix}insulin_string`,
          {
            name: insulinName,
            precision,
          },
        )

        // TODO all ids will be strings in the future
        if (accountInsulin.id === `${originalInsulin?.account_insulin_id}`) {
          activityHasMatch = true
        }

        return {
          value: accountInsulin.id,
          // TODO we are not yet localizing insulin names
          children: insulinString,
        }
      })

      // if the insulin activity is not included in the current account
      // insulins then add it as a disabled option
      if (!activityHasMatch && originalInsulin?.account_insulin_id) {
        const insulinName = originalInsulin.name

        const precision = polyglot.t(
          "unit.num_units_abbreviated",
          { units: insulinPrecisionToUnits(originalInsulin.precision ?? 1).toLocaleString(polyglot.locale()) },
        )

        const insulinString = polyglot.t(
          `${polyglotPrefix}insulin_string`,
          {
            name: insulinName,
            precision,
          },
        )

        optionsArray.push({
          value: `${originalInsulin.account_insulin_id}`,
          children: insulinString,
        })
      }

      return [
        defaultOption,
        ...optionsArray,
      ]
    },
    [
      originalInsulin,
      accountInsulins,
      polyglot,
    ],
  )

  return selectOptions
}
